import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatNumber } from "src/utils/numberFormat";
import "./TablePagePagination.scss";

export interface TablePagePaginationProps {
  /** 1-based current page index */
  currentPage: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
  className?: string;
}

/**
 * Shared table footer: “Page X of Y” + prev/next chevrons (same pattern as paid media dashboards).
 */
const TablePagePagination: FC<TablePagePaginationProps> = ({
  currentPage,
  totalPages,
  onPrevious,
  onNext,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const safeTotal = Math.max(1, totalPages);
  const displayCurrent = Math.min(Math.max(1, currentPage), safeTotal);
  const isArabic = i18n.language?.startsWith("ar");
  const PreviousIcon = isArabic ? ChevronRight : ChevronLeft;
  const NextIcon = isArabic ? ChevronLeft : ChevronRight;

  const label = t("pagination.pageOf", {
    current: formatNumber(displayCurrent, {}, i18n.language),
    total: formatNumber(safeTotal, {}, i18n.language),
  });

  return (
    <div className={`table-page-pagination ${className ?? ""}`.trim()}>
      <span className="table-page-pagination__label">{label}</span>
      <div className="table-page-pagination__nav">
        <button
          type="button"
          className="table-page-pagination__btn"
          onClick={onPrevious}
          disabled={displayCurrent <= 1}
          aria-label={t("pagination.previousPage")}
        >
          <PreviousIcon size={16} aria-hidden />
        </button>
        <button
          type="button"
          className="table-page-pagination__btn"
          onClick={onNext}
          disabled={displayCurrent >= safeTotal}
          aria-label={t("pagination.nextPage")}
        >
          <NextIcon size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
};

export default TablePagePagination;
